import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings as SettingsIcon, Save, IndianRupee, Calendar, MessageCircle, MessageSquare, Cloud, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const [settings, setSettings] = useState({
    monthly_pricing: '',
    yearly_pricing: '',
    free_trial_days: ''
  });
  const [creds, setCreds] = useState({
    whatsapp_api_token: '',
    whatsapp_phone_number_id: '',
    whatsapp_template_id: '',
    whatsapp_header_image_url: '',
    sms_auth_key: '',
    sms_template_id: '',
    dropbox_access_token: '',
    dropbox_backup_folder: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [savedCreds, setSavedCreds] = useState(false);
  const [showWhatsappToken, setShowWhatsappToken] = useState(false);
  const [showSmsKey, setShowSmsKey] = useState(false);
  const [showDropboxToken, setShowDropboxToken] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, credsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/settings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/settings/credentials`, { withCredentials: true }),
      ]);
      setSettings({
        monthly_pricing: settingsRes.data.monthly_pricing || '',
        yearly_pricing: settingsRes.data.yearly_pricing || '',
        free_trial_days: settingsRes.data.free_trial_days || ''
      });
      setCreds(credsRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(
        `${API_URL}/api/admin/settings`,
        {
          monthly_pricing: settings.monthly_pricing ? parseFloat(settings.monthly_pricing) : 0,
          yearly_pricing: settings.yearly_pricing ? parseFloat(settings.yearly_pricing) : 0,
          free_trial_days: settings.free_trial_days ? parseInt(settings.free_trial_days) : 0
        },
        { withCredentials: true }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCreds = async () => {
    setSavingCreds(true);
    setSavedCreds(false);
    try {
      await axios.put(`${API_URL}/api/admin/settings/credentials`, creds, { withCredentials: true });
      setSavedCreds(true);
      setTimeout(() => setSavedCreds(false), 3000);
    } catch (error) {
      console.error('Failed to save credentials:', error);
    } finally {
      setSavingCreds(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  const TokenInput = ({ id, value, onChange, placeholder, show, onToggle }) => (
    <div className="relative">
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="rounded-xl pr-10"
        data-testid={id}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Settings</h1>
        <p className="font-body text-text-secondary">Configure application settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
        {/* Subscription Settings */}
        <div className="bg-white rounded-2xl border border-surface-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center">
              <SettingsIcon className="text-brand-primary" size={20} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-text-primary">Subscription Settings</h2>
              <p className="font-body text-sm text-text-muted">Configure pricing and trial period</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="monthly_pricing" className="font-body font-medium flex items-center gap-2">
                <IndianRupee size={16} className="text-text-muted" />
                Monthly Pricing
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted font-medium">&#8377;</span>
                <Input
                  id="monthly_pricing"
                  type="number"
                  value={settings.monthly_pricing}
                  onChange={(e) => setSettings({ ...settings, monthly_pricing: e.target.value })}
                  placeholder="0.00"
                  className="rounded-xl pl-8"
                />
              </div>
              <p className="text-xs text-text-muted">Set the monthly subscription price for vendors</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="yearly_pricing" className="font-body font-medium flex items-center gap-2">
                <IndianRupee size={16} className="text-text-muted" />
                Yearly Pricing
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted font-medium">&#8377;</span>
                <Input
                  id="yearly_pricing"
                  type="number"
                  value={settings.yearly_pricing}
                  onChange={(e) => setSettings({ ...settings, yearly_pricing: e.target.value })}
                  placeholder="0.00"
                  className="rounded-xl pl-8"
                />
              </div>
              <p className="text-xs text-text-muted">Set the yearly subscription price for vendors</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="free_trial_days" className="font-body font-medium flex items-center gap-2">
                <Calendar size={16} className="text-text-muted" />
                Free Trial Period
              </Label>
              <div className="relative">
                <Input
                  id="free_trial_days"
                  type="number"
                  value={settings.free_trial_days}
                  onChange={(e) => setSettings({ ...settings, free_trial_days: e.target.value })}
                  placeholder="0"
                  className="rounded-xl pr-16"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted font-medium">days</span>
              </div>
              <p className="text-xs text-text-muted">Number of days for free trial before subscription starts</p>
            </div>

            <div className="pt-4 flex items-center gap-4">
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Settings
                  </>
                )}
              </Button>
              {saved && (
                <span className="text-green-600 font-body text-sm">Settings saved successfully!</span>
              )}
            </div>
          </div>
        </div>
        </div>{/* end left column */}

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
        {/* WhatsApp Configuration */}
        <div className="bg-white rounded-2xl border border-surface-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <MessageCircle className="text-green-600" size={20} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-text-primary">WhatsApp Configuration</h2>
              <p className="font-body text-sm text-text-muted">Configure WhatsApp Business API credentials</p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="whatsapp_api_token" className="font-body font-medium">API Token</Label>
              <TokenInput
                id="whatsapp_api_token"
                value={creds.whatsapp_api_token}
                onChange={(e) => setCreds({ ...creds, whatsapp_api_token: e.target.value })}
                placeholder="Enter WhatsApp API Token"
                show={showWhatsappToken}
                onToggle={() => setShowWhatsappToken(!showWhatsappToken)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp_phone_number_id" className="font-body font-medium">Phone Number ID</Label>
              <Input
                id="whatsapp_phone_number_id"
                value={creds.whatsapp_phone_number_id}
                onChange={(e) => setCreds({ ...creds, whatsapp_phone_number_id: e.target.value })}
                placeholder="e.g. 937349779458170"
                className="rounded-xl"
                data-testid="whatsapp_phone_number_id"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp_template_id" className="font-body font-medium">Template ID</Label>
              <Input
                id="whatsapp_template_id"
                value={creds.whatsapp_template_id}
                onChange={(e) => setCreds({ ...creds, whatsapp_template_id: e.target.value })}
                placeholder="e.g. gowda_egg_wa_template"
                className="rounded-xl"
                data-testid="whatsapp_template_id"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp_header_image_url" className="font-body font-medium">Header Image URL</Label>
              <Input
                id="whatsapp_header_image_url"
                value={creds.whatsapp_header_image_url}
                onChange={(e) => setCreds({ ...creds, whatsapp_header_image_url: e.target.value })}
                placeholder="https://example.com/logo.png"
                className="rounded-xl"
                data-testid="whatsapp_header_image_url"
              />
              <p className="text-xs text-text-muted">Image displayed in WhatsApp message header (recommended: 300x300px)</p>
            </div>
          </div>
        </div>

        {/* SMS Configuration */}
        <div className="bg-white rounded-2xl border border-surface-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <MessageSquare className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-text-primary">SMS Configuration (MSG91)</h2>
              <p className="font-body text-sm text-text-muted">Configure MSG91 API credentials for SMS notifications</p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="sms_auth_key" className="font-body font-medium">Auth Key</Label>
              <TokenInput
                id="sms_auth_key"
                value={creds.sms_auth_key}
                onChange={(e) => setCreds({ ...creds, sms_auth_key: e.target.value })}
                placeholder="Enter MSG91 Auth Key"
                show={showSmsKey}
                onToggle={() => setShowSmsKey(!showSmsKey)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sms_template_id" className="font-body font-medium">Template ID</Label>
              <Input
                id="sms_template_id"
                value={creds.sms_template_id}
                onChange={(e) => setCreds({ ...creds, sms_template_id: e.target.value })}
                placeholder="Enter MSG91 Template ID"
                className="rounded-xl"
                data-testid="sms_template_id"
              />
            </div>
          </div>
        </div>

        {/* Dropbox Configuration */}
        <div className="bg-white rounded-2xl border border-surface-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Cloud className="text-indigo-600" size={20} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-text-primary">Dropbox Configuration</h2>
              <p className="font-body text-sm text-text-muted">Configure Dropbox API credentials for automatic backups</p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="dropbox_access_token" className="font-body font-medium">Access Token</Label>
              <TokenInput
                id="dropbox_access_token"
                value={creds.dropbox_access_token}
                onChange={(e) => setCreds({ ...creds, dropbox_access_token: e.target.value })}
                placeholder="Enter Dropbox Access Token"
                show={showDropboxToken}
                onToggle={() => setShowDropboxToken(!showDropboxToken)}
              />
              <p className="text-xs text-text-muted">
                Generate a token from the{' '}
                <a href="https://dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">
                  Dropbox App Console
                </a>{' '}
                (dropbox.com/developers/apps)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dropbox_backup_folder" className="font-body font-medium">Backup Folder Path</Label>
              <Input
                id="dropbox_backup_folder"
                value={creds.dropbox_backup_folder}
                onChange={(e) => setCreds({ ...creds, dropbox_backup_folder: e.target.value })}
                placeholder="/Backups/GowdaEggDistributors/"
                className="rounded-xl"
                data-testid="dropbox_backup_folder"
              />
              <p className="text-xs text-text-muted">Dropbox folder where backups will be stored (e.g., /Backups/EggDistributors/)</p>
            </div>
          </div>
        </div>

        {/* Save Credentials Button */}
        <div className="flex items-center gap-4">
          <Button
            onClick={handleSaveCreds}
            disabled={savingCreds}
            className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
            data-testid="save-credentials-btn"
          >
            {savingCreds ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Credentials
              </>
            )}
          </Button>
          {savedCreds && (
            <span className="text-green-600 font-body text-sm">Credentials saved successfully!</span>
          )}
        </div>
        </div>{/* end right column */}
      </div>
    </div>
  );
};

export default Settings;
